import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import config from 'config'
import videojs from 'video.js'
import 'video.js/dist/video-js.css'
import { AbsoluteFillStyle } from 'theme'
import styled from 'styled-components'

const propTypes = {
  videoUrl: PropTypes.string.isRequired,
  playbackVideoUrl: PropTypes.string.isRequired,
  onFinish: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  updateState: PropTypes.func,
  draftMode: PropTypes.bool,
  timedInterview: PropTypes.bool,
  pauseVideo: PropTypes.bool,
}

const defaultProps = {
  updateState: () => {},
  draftMode: false,
  pauseVideo: false,
  timedInterview: false,
}

const VideoPlayerContainer = styled.div`
  .video-js[tabindex='-1'] {
    ${AbsoluteFillStyle};
    display: block;
    cursor: pointer;
    z-index: 1;
  }

  .vjs-control-bar {
    display: flex;
  }

  .vjs-current-time,
  .vjs-time-divider,
  .vjs-duration {
    display: flex;
    align-items: center;
    font-size: ${({ theme }) => theme.space.s12};
  }

  .vjs-current-time,
  .vjs-duration {
    padding: 0;
  }
`

const StyledPlayer = styled.div`
  display: block;
  cursor: pointer;
  ${AbsoluteFillStyle};
`

const VideoPlayer = ({
  videoUrl,
  playbackVideoUrl,
  onFinish,
  onError,
  updateState,
  draftMode,
  timedInterview,
  pauseVideo,
}) => {
  let videoNode = React.createRef(null)
  let player

  const videoUrlToHlsUrl = url => {
    return url.replace('?', '.flv.mp4/playlist.m3u8?')
  }

  const getSources = (playbackVideoUrl, videoUrl) => {
    // You can set useHls in config to true if you need to view flash-recordings on localhost
    if (config.USE_HLS) {
      return [
        {
          type: 'application/x-mpegurl',
          src: `${config.VIDEO_URL_BASE}/${videoUrlToHlsUrl(videoUrl)}`,
        },
      ]
    }
    return [
      {
        type: 'video/mp4',
        src: playbackVideoUrl,
      },
    ]
  }

  const unloadPlayer = () => {
    if (player) {
      player.dispose()
      videoNode = null
    }
  }

  const loadPlayer = () => {
    const imageName =
      videoUrl.indexOf('archive.mp4') > -1
        ? videoUrl.split('/')[1]
        : videoUrl.replace(/\?.*$/, '')
    const videoJsOptions = {
      techOrder: ['html5'],
      playbackRates: [0.5, 1, 1.5, 2],
      bigPlayButton: false,
      controlBar: {
        children: {
          playToggle: true,
          currentTimeDisplay: true,
          timeDivider: true,
          durationDisplay: true,
          progressControl: true,
          fullscreenToggle: true,
          playbackRateMenuButton: true,
        },
      },
      poster: `${config.USER_UPLOADS_BASE_URL}/b2c-screenshots/${imageName}.jpg`,
      controls: !draftMode && !timedInterview,
      userActions: {
        doubleClick: false,
      },
      sources: getSources(playbackVideoUrl, videoUrl),
    }

    player = videojs(videoNode.current, videoJsOptions)
    player.on('error', () => {
      // Code 4 is case when video is not found which usually means it is not converted yet
      if (player.error().code === 4) {
        // Because VideoJS is manipulating dom reloading is easier to handle on parent
        // (unmounting and remounting component by parent)
        onError()
      }
    })

    player.on('ready', () => {
      player.play()
      if (videoNode && videoNode.current) {
        videoNode.current.play()
      }
    })

    player.on('play', event => {
      const videos = Array.prototype.slice.apply(
        document.getElementsByTagName('video')
      )

      videos.forEach(video => {
        if (event.target.firstChild !== video) {
          video.pause()
        }
      })
      updateState('play')
    })

    player.on('pause', () => {
      if (draftMode) {
        onFinish()
      } else if (!timedInterview) {
        // don't update state to pause if on timed interview as it's immediately played again
        // and pause state is checked to move candidate forward when video ends
        updateState('pause')
      }
    })

    if (timedInterview) {
      // if on timed interview pausing should not work so call play on pause
      player.on('pause', () => {
        if (player.paused()) {
          player.play()
        }
      })
      // these are for disabling seeking (dragging time)
      // when video opens in the native video player on iOS
      let previousTime = 0

      player.on('timeupdate', () => {
        if (!player.seeking()) {
          previousTime = player.currentTime()
        }
      })

      player.on('seeking', () => {
        if (
          player.currentTime() > previousTime ||
          player.currentTime() < previousTime
        ) {
          player.currentTime(previousTime)
        }
      })
    }

    player.on('ended', () => {
      onFinish()
    })
  }

  useEffect(() => {
    loadPlayer()
    return () => {
      unloadPlayer()
    }
  }, [])

  useEffect(() => {
    if (pauseVideo && player) {
      player.pause()
    }
  }, [pauseVideo])

  return (
    <VideoPlayerContainer>
      <StyledPlayer data-vjs-player>
        <video ref={videoNode} className="video-js" />
      </StyledPlayer>
    </VideoPlayerContainer>
  )
}

VideoPlayer.propTypes = propTypes
VideoPlayer.defaultProps = defaultProps

export default VideoPlayer
